/**
 * Workflow Document Requirements Page
 *
 * Per-tenant admin page that toggles whether each document-uploading
 * workflow requires an attached document before it can proceed.
 *
 * Backed by the workflow_document_requirements table (T099); flipping a
 * switch calls PUT /api/workflow-document-requirements/:workflow_code.
 *
 * Permission: super-admin only by default (the WORKFLOW_DOCUMENT_REQUIREMENTS
 * module has no seeded role grants; only is_admin=true users see it).
 */

import React, { useEffect, useState } from 'react';
import {
  Box,
  Paper,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  CircularProgress,
  Switch,
  Chip,
  Tooltip,
} from '@mui/material';
import RuleIcon from '@mui/icons-material/Rule';
import { apiGet, apiPut } from '../../utils/apiClient';
import useAsyncData from '../../hooks/useAsyncData';
import { useNotification } from '../../contexts/NotificationContext';

function WorkflowDocumentRequirementsPage() {
  const notify = useNotification();
  const { data: rows, loading, error: loadError, reload } = useAsyncData(
    () => apiGet('/workflow-document-requirements'),
    { defaultValue: [] }
  );

  const [savingCode, setSavingCode] = useState(null);
  // Optimistic local override so the switch flips immediately while the
  // PUT is in flight; reverted on error.
  const [optimistic, setOptimistic] = useState({});

  useEffect(() => {
    if (loadError) notify.error(loadError);
    // eslint-disable-next-line react-hooks/exhaustive-deps -- notify stable
  }, [loadError]);

  const handleToggle = async (row, nextValue) => {
    setSavingCode(row.workflow_code);
    setOptimistic((prev) => ({ ...prev, [row.workflow_code]: nextValue }));
    try {
      await apiPut(`/workflow-document-requirements/${row.workflow_code}`, { is_required: nextValue });
      notify.success(`Updated ${row.display_name}`);
      await reload();
      setOptimistic((prev) => {
        const next = { ...prev };
        delete next[row.workflow_code];
        return next;
      });
    } catch (err) {
      const msg = err?.response?.data?.error?.message || err?.message || 'Failed to update workflow';
      notify.error(msg);
      // Revert the optimistic flip
      setOptimistic((prev) => {
        const next = { ...prev };
        delete next[row.workflow_code];
        return next;
      });
    } finally {
      setSavingCode(null);
    }
  };

  return (
    <Box>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
        <RuleIcon />
        <Typography variant="h4">Workflow Document Requirements</Typography>
      </Box>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
        Each row is a workflow that uploads a document. Turn on Required to
        force the workflow to refuse any submission that arrives without an
        attached document. The Document Type column is informational — it
        identifies the kind of document the workflow consumes.
      </Typography>

      {loading ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 6 }}>
          <CircularProgress />
        </Box>
      ) : (
        <Paper>
          <TableContainer>
            <Table>
              <TableHead>
                <TableRow>
                  <TableCell>Workflow</TableCell>
                  <TableCell>Document Type</TableCell>
                  <TableCell align="center">Required</TableCell>
                  <TableCell>Description</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {rows.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={4} align="center">
                      <Typography variant="body2" color="text.secondary" sx={{ py: 4 }}>
                        No workflows registered.
                      </Typography>
                    </TableCell>
                  </TableRow>
                ) : (
                  rows.map((row) => {
                    const checked = Object.prototype.hasOwnProperty.call(optimistic, row.workflow_code)
                      ? optimistic[row.workflow_code]
                      : row.is_required === true;
                    return (
                      <TableRow key={row.workflow_code}>
                        <TableCell>
                          <Typography variant="body2" fontWeight="medium">{row.display_name}</Typography>
                          <Typography variant="caption" color="text.secondary">{row.workflow_code}</Typography>
                        </TableCell>
                        <TableCell>
                          <Chip
                            size="small"
                            label={row.document_type_name || row.document_type_code}
                          />
                          <Typography variant="caption" color="text.secondary" display="block">
                            {row.document_type_code}
                          </Typography>
                        </TableCell>
                        <TableCell align="center">
                          <Tooltip title={checked ? 'Document is required' : 'Document is optional'}>
                            <span>
                              <Switch
                                checked={checked}
                                onChange={(e) => handleToggle(row, e.target.checked)}
                                disabled={savingCode === row.workflow_code}
                              />
                            </span>
                          </Tooltip>
                        </TableCell>
                        <TableCell>
                          <Typography variant="body2" color="text.secondary">
                            {row.description}
                          </Typography>
                        </TableCell>
                      </TableRow>
                    );
                  })
                )}
              </TableBody>
            </Table>
          </TableContainer>
        </Paper>
      )}
    </Box>
  );
}

export default WorkflowDocumentRequirementsPage;
